import ErrorType from '@/enum/ErrorType'
import { ComponentError, NetworkError } from '@/models'
import useGlobalStore from '@/stores/modules/global'
import {
  APP_ENV,
  CANCELED_REQUEST_CODE,
  DASHBOARD_DOMAINS,
  NETWORK_ERROR_CODE
} from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import { logError } from '@/utils/helpers/logger'

type GlobalStoreSGA = ReturnType<typeof useGlobalStore>

class ErrorHandlerService {
  static setupGlobalErrorHandler(err: any, vm: any, info: any) {
    ErrorHandlerService.setupComponentErrorHandler(err, vm, info)
  }

  static setupComponentErrorHandler(err: any, vm: any, info: any) {
    const errorObject = new ComponentError()

    errorObject.name = err?.toString()

    if (vm?.$el?.classList?.value)
      errorObject.classes = `${CONSTANTS.ERROR_CLASSES} ${vm.$el.classList.value.toString()}`

    if (err?.stack) errorObject.stack = err.stack.toString()

    if (
      !errorObject?.name
        ?.toLocaleLowerCase()
        ?.split(' ')
        ?.some((error) => CONSTANTS.ERROR_IGNORES.includes(error))
    ) {
      ErrorHandlerService.handleError(errorObject, ErrorType.Component).catch((error) =>
        logError(error, error)
      )
    }
  }

  static handleNetworkError(error: any) {
    const errorObject = new NetworkError()

    if (error.code === CANCELED_REQUEST_CODE) return

    errorObject.name =
      error.code === NETWORK_ERROR_CODE
        ? CONSTANTS.NETWORK_ERROR_UNKNOWN
        : (APP_ENV === 'Development' ? error?.message : '') || ''

    if (error?.response?.data?.detail)
      errorObject.name += ` ${errorObject.name ? '|' : ''} ${error.response.data.detail}`

    errorObject.title = error?.response?.data?.message || error.message
    errorObject.stack = error?.response?.data?.stackTrace || error.stack

    if (error?.request?.responseURL) errorObject.requestedUrl = error.request.responseURL

    if (error?.response?.status) errorObject.statusCode = String(error.response.status)

    if (error?.config?.data) errorObject.payload = JSON.stringify(error.config.data)

    if (error?.config?.method) errorObject.requestMethod = error.config.method

    ErrorHandlerService.handleError(errorObject, ErrorType.Network).catch((loggingError) =>
      logError(JSON.stringify(errorObject), loggingError)
    )
  }

  static handleError(error: object, errorType: ErrorType) {
    const allowedClientErrorReportEnvironments = ['Development', 'Production', 'Staging', 'Demo']
    const allowedServerErrorReportEnvironments = ['Development', 'Production', 'Staging', 'Demo']

    if (allowedClientErrorReportEnvironments.includes(APP_ENV)) {
      ErrorHandlerService.reportErrorToClient(error, errorType)
    }

    if (allowedServerErrorReportEnvironments.includes(APP_ENV)) {
      const allowedDomains = [
        DASHBOARD_DOMAINS.DEVELOPMENT.URL,
        DASHBOARD_DOMAINS.STAGING.URL,
        DASHBOARD_DOMAINS.DEMO.URL
      ]
    }

    return Promise.reject(error)
  }

  static handleAndCatchError(error: object, errorType: ErrorType) {
    ErrorHandlerService.handleError(error, errorType).catch((error) => error)
  }

  static reportErrorToClient(error: object, errorType: ErrorType) {
    const globalStore: GlobalStoreSGA = useGlobalStore()
    globalStore.addError(error, errorType)
  }
}

export default ErrorHandlerService
